<script setup lang="ts">
import { ref, inject, computed, onBeforeMount, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash/cloneDeep';
import { DateTime } from 'luxon';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { STATUS_OPEN } from '@/constants';
import DestinationService from '@/service/DestinationService';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useToast } from 'primevue/usetoast';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import Fluid from 'primevue/fluid';
import MultiSelect from 'primevue/multiselect';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const dialogRef = inject('dialogRef') as Ref;
const dataEdit = dialogRef?.value.options.data?.data;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const PackageManagementStore = usePackageManagementStore();

const destinations = computed(() => PackageManagementStore.destinations);

const destinationGroup = ref(
  dataEdit
    ? cloneDeep(dataEdit)
    : {
        airportCode: '',
        destCode: '',
        destName: '',
        groupDes: [
          {
            code: '',
            name: ''
          }
        ],
        status: STATUS_OPEN,
        created: new Date().getUTCDate()
      }
);

const rules = {
  airportCode: { required },
  destCode: { required },
  groupDes: [
    {
      code: { required },
      name: { required }
    }
  ]
};

const selectMapDestination = ref([]);
if (dataEdit) {
  selectMapDestination.value = dataEdit.groupDes?.map((destination) => destination.code);
}

const v$ = useVuelidate(rules, destinationGroup.value);
const airportCode = ref(dataEdit?.airportCode || '');
const destinationNameId = ref(dataEdit ? `${dataEdit?.destName} (${dataEdit?.destCode})` : '');
const closeModal = dialogRef?.value.options.emits.closeModal;
const filterAirportcodeResult = ref([]);
const filterDestinationResult = ref([]);
const filterDestinationMapResult = ref([]);
const overlapList = ref([]);
const isLoading = ref(false);

const searchAirPort = (event, destinations) => {
  let i = 0;
  const target = [...destinations];
  while (i !== target.length) {
    const dupplicatePassengerIndex = target.findIndex((destination, index) => index !== i && destination.airportCode === target[i].airportCode);
    if (dupplicatePassengerIndex === -1) {
      i += 1;
    } else {
      target.splice(dupplicatePassengerIndex, 1);
    }
  }
  if (!event.query.trim().length) {
    filterAirportcodeResult.value = [...target];
  } else {
    filterAirportcodeResult.value = target.filter((destination) => {
      return destination.airportCode?.toLowerCase().startsWith(event.query.toLowerCase());
    });
  }
};

const formatDestination = (listDestination, desGroup) => {
  const dataList = cloneDeep(listDestination);
  return dataList.filter((item) => item.airportCode === desGroup.airportCode);
};

const filterGroupDestination = (listDestination, desGroup) => {
  const dataList = cloneDeep(listDestination);
  return dataList.filter((item) => item.country === desGroup.countryName);
};

const searchDestination = async (event, destination) => {
  if (!event.query.trim().length) {
    filterDestinationResult.value = formatDestination([...destination], destinationGroup.value);
  } else {
    const filterResult = destination.filter((des) => {
      return des.code?.toLowerCase().startsWith(event.query.toLowerCase()) || des.name?.toLowerCase().startsWith(event.query.toLowerCase());
    });
    filterDestinationResult.value = formatDestination(filterResult, destinationGroup.value);
  }
};

const selectDestinationMulMap = () => {
  filterDestinationMapResult.value = filterGroupDestination([...destinations.value], destinationGroup.value);
};

const selectAirportCode = (e) => {
  destinationGroup.value.destCode = '';
  destinationGroup.value.destName = '';
  destinationGroup.value.groupDes = [];
  destinationNameId.value = '';
  selectMapDestination.value = [];
  destinationGroup.value.countryName = '';
  if (e.value && typeof e.value === 'object') {
    destinationGroup.value.airportCode = e.value.airportCode;
    destinationGroup.value.countryName = e.value.country;
  } else {
    destinationGroup.value.airportCode = '';
    destinationGroup.value.countryName = '';
  }
};

const selectDestination = (e) => {
  if (e.value && typeof e.value === 'object') {
    destinationGroup.value.destCode = e.value.code;
    destinationGroup.value.destName = e.value.name;
    destinationNameId.value = `${e.value.name} (${e.value.code})`;
  } else {
    destinationGroup.value.destCode = '';
    destinationGroup.value.destName = '';
  }
};

const selectDestinationMap = (e) => {
  if (e.value) {
    destinationGroup.value.groupDes = filterDestinationMapResult.value.filter((destination) => e.value.includes(destination.code));
  } else {
    destinationGroup.value.groupDes = [];
  }
};

const validateGroupDes = (groupDes) => {
  if (!groupDes) {
    return;
  }
  let isValid = true;

  for (const item of groupDes) {
    const itemValidation = useVuelidate(
      {
        code: { required: required },
        name: { required: required }
      },
      item
    );

    if (itemValidation.$error) {
      isValid = false;
    }
  }
  return isValid;
};

const handleSubmitAdd = async () => {
  v$.value.$validate();
  const isGroupDesValid = validateGroupDes(destinationGroup.value.groupDes);
  if (!v$.value?.$error && isGroupDesValid) {
    try {
      const formattedUTC = DateTime.utc().toISO();
      if (!dataEdit) {
        destinationGroup.value.created = formattedUTC;
      }
      await new DestinationService().addDestinationsGroup(destinationGroup.value);
      closeModal();
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      airportCode.value = '';
      destinationNameId.value = '';
      selectMapDestination.value = [];
    } catch (error) {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    }
  }
};

onBeforeMount(async () => {
  await Promise.all([PackageManagementStore.getListDestination()]);
  if (dataEdit) {
    filterDestinationResult.value = formatDestination([...destinations.value], destinationGroup.value);
    filterDestinationMapResult.value = filterGroupDestination([...destinations.value], destinationGroup.value);
  }
});
</script>
<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="manage-destination-grouping-modal">
    <Fluid>
      <div class="field">
        <label for="dest">{{ $t('management.package.airport_code') }}</label>
        <div class="flex align-items-center">
          <AutoComplete
            v-model="airportCode"
            :suggestions="filterAirportcodeResult"
            optionLabel="airportCode"
            @complete="searchAirPort($event, destinations)"
            @change="selectAirportCode($event)"
            field="airportCode"
            optionValue="airportCode"
            :completeOnFocus="true"
            :placeholder="$t('management.package.airport_code')"
            class="w-full"
            forceSelection
            :class="v$.airportCode.$error ? 'p-invalid' : ''"
          >
            <template #option="slotProps">
              <div class="flex align-options-center">
                <div>
                  <div>{{ slotProps.option.airportCode }}</div>
                </div>
              </div>
            </template>
          </AutoComplete>
        </div>
        <p class="text-red-500 pt-1" v-if="v$.airportCode.$error">{{ $t('validate.required') }}</p>
      </div>
    </Fluid>
    <Fluid>
      <div class="field">
        <label for="dest">{{ $t('management.package.destination_id') }}</label>
        <div class="flex align-items-center">
          <AutoComplete
            v-model="destinationNameId"
            :suggestions="filterDestinationResult"
            optionLabel="name"
            :disabled="!airportCode"
            @complete="searchDestination($event, destinations)"
            optionValue="value"
            :completeOnFocus="true"
            @change="selectDestination($event)"
            :placeholder="$t('management.package.destination_id')"
            class="w-full"
            :class="v$.destCode.$error ? 'p-invalid' : ''"
            forceSelection
          >
            <template #option="slotProps">
              <div class="ml-2">{{ slotProps.option.name }} - ({{ slotProps.option.code }})</div>
            </template>
          </AutoComplete>
        </div>
        <p class="text-red-500 pt-1" v-if="v$.destCode.$error">{{ $t('validate.required') }}</p>
      </div>
    </Fluid>
    <Fluid>
      <div class="field">
        <label for="dest">{{ $t('management.package.mapped_destination') }}</label>
        <div class="flex align-items-center">
          <MultiSelect
            :completeOnFocus="true"
            v-model="selectMapDestination"
            :options="filterDestinationMapResult"
            :disabled="!destinationNameId"
            :maxSelectedLabels="4"
            @focus="selectDestinationMulMap"
            optionLabel="name"
            optionValue="code"
            filter
            :placeholder="$t('management.package.mapped_destination')"
            display="chip"
            :class="v$.groupDes.$error ? 'p-invalid w-full' : 'w-full'"
            @change="selectDestinationMap($event)"
          >
            <template #option="slotProps">
              <div class="flex align-items-center">
                <div>{{ slotProps.option.name }} - ({{ slotProps.option.code }})</div>
              </div>
            </template>
          </MultiSelect>
        </div>
        <p class="text-red-500 pt-1" v-if="v$.groupDes.$error">{{ $t('validate.required') }}</p>
      </div>
    </Fluid>
    <div class="flex justify-content-center mt-3">
      <Button @click="handleSubmitAdd" :label="$t('voucher.submit')" />
    </div>
    <div v-if="overlapList.length" class="border-2 p-2 border-red-500 border-round-md mt-3">
      <div class="text-center font-bold text-2xl text-900 mb-4">
        <span>{{ $t('management.package.alert') }}!</span>
      </div>
      <div class="text-center mb-4 text-700">
        <span>{{ $t('management.package.alert_message') }}!</span>
      </div>
      <div class="text-700">
        <div v-for="(overlap, overlapIndex) in overlapList" :key="'overlap' + overlapIndex">- ({{ overlap.gateName }} - {{ overlap.desName }}) - ({{ overlap.depDateStart }} - {{ overlap.depDateEnd }})</div>
      </div>
    </div>
  </div>
</template>
